import { SetStateAction, useEffect, useState } from 'react';

import {
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubIconButton,
  DevHubInputAdornment,
  DevHubLink,
  DevHubPaper,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import {
  DataTable,
  DataTableExpandedRows,
  DataTableSortEvent,
  DataTableValueArray,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Content, Header, Page } from '@backstage/core-components';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import { useApolloClient } from '@apollo/client';
import { GetComponentsQuery } from './queries';
import {
  CatalogQueryType,
  CatalogSearchResultData,
  CatalogSearchResultItem,
  CatalogStandardMapping,
  ResourceCatalogComponentPaged,
} from './types';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
  ComponentDetails,
  convertComponentSearchResult,
} from './ComponentDetails';
import { ownerOrgRenderer } from './fieldRenderers/ownerOrg';
import { descriptionRenderer } from './fieldRenderers/description';

const searchResultConverters = {
  [CatalogQueryType.TechDocs]: (data: ResourceCatalogComponentPaged) =>
    convertComponentSearchResult(data, CatalogQueryType.TechDocs),
};

const queries = {
  [CatalogQueryType.TechDocs]: GetComponentsQuery,
};

const columns: {
  [CatalogQueryType.TechDocs]: {
    renderer: any;
    field: string;
    width: string;
    header: string;
  }[];
} = {
  [CatalogQueryType.TechDocs]: [
    {
      field: 'ownerOrg',
      header: 'Owner',
      width: '25%',
      renderer: ownerOrgRenderer,
    },
    {
      field: 'description',
      header: 'Description',
      width: '30%',
      renderer: descriptionRenderer,
    },
  ],
};

const rowDetailForRow = (row: CatalogSearchResultItem) => {
  return <ComponentDetails {...row} />;
};

const linkTemplateForRow = (row: CatalogSearchResultItem) => {
  return (
    <DevHubLink component={NavLink} to={`/docs/default/component/${row?.id}`}>
      {row?.name}
    </DevHubLink>
  );
};

const queryParamsForType = (searchTerm?: string) => {
  return {
    filter: {
      name: searchTerm ? `*${searchTerm}*` : undefined,
      hasTechDocsRef: true,
    },
  };
};

function mapToStandardType(
  props: CatalogStandardMapping,
): CatalogSearchResultData {
  const { data } = props;
  return searchResultConverters[CatalogQueryType.TechDocs](data as any);
}

/**
 * DevHub Documentation Home Page component
 * @constructor
 */
export const DocumentationHome = () => {
  const ITEMS_PER_PAGE = 20;
  const client = useApolloClient();
  const [searchParams] = useSearchParams();
  const [firstItem, setFirstItem] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isStarred, setIsStarred] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchClicked, setSearchClicked] = useState(false);
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [data, setData] = useState<CatalogSearchResultItem[]>(
    [] as CatalogSearchResultItem[],
  );
  const [originalData, setOriginalData] = useState<CatalogSearchResultItem[]>(
    [] as CatalogSearchResultItem[],
  );
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    const params = queryParamsForType(
      searchTerm === '' ? undefined : searchTerm,
    );

    setIsFetching(true);
    client
      .query({
        query: queries[CatalogQueryType.TechDocs],
        variables: {
          pagination: {
            limit: ITEMS_PER_PAGE,
            offset: currentPage * ITEMS_PER_PAGE,
          },
          ...params,
        },
      })
      .then(response => {
        const result = {
          data: response.data,
          queryType: CatalogQueryType.TechDocs,
        } as unknown as CatalogStandardMapping;

        const standardData = mapToStandardType(result);
        let items = standardData.items;
        if (showStarredOnly) {
          const starredDocs = JSON.parse(
            localStorage.getItem('starredDocumentation') || '[]',
          );
          items = items.filter(item => starredDocs.includes(item.id));
          setTotalRecords(items.length);
        } else {
          setTotalRecords(standardData.totalCount);
        }
        setData(items);
        setOriginalData(items);
      })
      .finally(() => setIsFetching(false));

    setSearchClicked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchClicked, searchParams, showStarredOnly]);

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  const toggleStar = (rowDataID: any) => {
    const currentArray = localStorage.getItem('starredDocumentation');
    const newArray = currentArray ? JSON.parse(currentArray) : [];

    const index = newArray.indexOf(rowDataID);
    if (index !== -1) {
      newArray.splice(index, 1);
    } else {
      newArray.push(rowDataID);
    }
    localStorage.setItem('starredDocumentation', JSON.stringify(newArray));
    setIsStarred(!isStarred);
  };

  const starButtonTemplate = (rowData: any) => {
    const isDocStarred = JSON.parse(
      localStorage.getItem('starredDocumentation') || '[]',
    ).includes(rowData.id);

    return (
      <DevHubBox display="flex" alignItems="center" justifyContent="center">
        <DevHubIconButton onClick={() => toggleStar(rowData.id)}>
          <DevHubDynamicIcon
            iconName="StarSharp"
            fontSize="medium"
            color={isDocStarred ? 'gold' : 'disabled'}
          />
        </DevHubIconButton>
      </DevHubBox>
    );
  };

  const onPaginate = (e: { first: SetStateAction<number> }) => {
    setCurrentPage((e.first as number) / ITEMS_PER_PAGE);
    setFirstItem(e.first);
    window.scrollTo(0, 0);
  };

  const resetPagination = () => {
    window.scrollTo(0, 0);
    setCurrentPage(0);
    setFirstItem(0);
  };

  const handleSearch = () => {
    resetPagination();
    setSearchClicked(true);
  };

  const clearSearch = () => {
    setSearchTerm('');

    if (searchTerm !== '') {
      handleSearch();
    }
  };
  const customSort = (event: DataTableSortEvent) => {
    if (event.sortField === 'starred') {
      setShowStarredOnly(!showStarredOnly);
      resetPagination();
      setSearchClicked(true);
    } else {
      const sortedOrder = event.sortOrder ?? 0;

      const sortedData = [...originalData].sort((a, b) => {
        const aStarred = JSON.parse(
          localStorage.getItem('starredDocumentation') || '[]',
        ).includes(a.id);
        const bStarred = JSON.parse(
          localStorage.getItem('starredDocumentation') || '[]',
        ).includes(b.id);
        if (aStarred === bStarred) return 0;
        return aStarred ? -1 * sortedOrder : 1 * sortedOrder;
      });
      setData(sortedData);
    }
  };

  return (
    <Page themeId="home">
      <Header
        title="Documentation"
        style={{ whiteSpace: 'nowrap' }}
        subtitle="Documentation available in DevHub"
      >
        <DevHubBox display="flex" alignItems="center" justifyContent="flex-end">
          <DevHubButton
            variant="text"
            textColor="white"
            size="large"
            height="large"
            href="/catalog/default/component/b14e50528c366325869c3a720429f270e5fccc31/docs/documentation"
          >
            <DevHubDynamicIcon iconName="Help" />
            Add Documentation&nbsp;&nbsp;
          </DevHubButton>
        </DevHubBox>
      </Header>
      <Content>
        <DevHubPaper>
          <DevHubGrid container>
            <DevHubGrid item xs={12}>
              <DevHubBox sx={{ minHeight: '15rem', width: '100%' }}>
                <div className="card">
                  <div style={{ position: 'relative' }}>
                    <Backdrop
                      open={isFetching}
                      style={{
                        color: '#fff',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          margin: '10rem auto',
                          width: 'fit-content',
                        }}
                      >
                        <CircularProgress color="inherit" />
                      </div>
                    </Backdrop>
                    <DevHubBox display="flex" paddingBottom={1}>
                      <DevHubGrid
                        container
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <DevHubGrid item sm={12} md={3}>
                          <DevHubTextField
                            id="techdocs-search"
                            label="Search"
                            margin="normal"
                            variant="outlined"
                            value={searchTerm}
                            disabled={isFetching}
                            noMargin
                            placeholder="Enter your search text"
                            onChange={(e: {
                              target: { value: SetStateAction<string> };
                            }) => setSearchTerm(e.target.value)}
                            onKeyDown={(e: { key: string }) =>
                              e.key === 'Enter' && handleSearch()
                            }
                            size="small"
                            InputProps={
                              searchTerm
                                ? {
                                    endAdornment: (
                                      <DevHubInputAdornment position="end">
                                        <DevHubIconButton onClick={clearSearch}>
                                          <DevHubDynamicIcon iconName="Clear" />
                                        </DevHubIconButton>
                                      </DevHubInputAdornment>
                                    ),
                                  }
                                : {}
                            }
                          />
                        </DevHubGrid>
                        <DevHubGrid item sm={12} md={1}>
                          <DevHubButton
                            variant="contained"
                            buttonColor="black"
                            size="large"
                            height="large"
                            href="#"
                            disabled={isFetching}
                            onClick={handleSearch}
                          >
                            <DevHubDynamicIcon iconName="Search" />
                          </DevHubButton>
                        </DevHubGrid>
                      </DevHubGrid>
                    </DevHubBox>
                    <DataTable
                      value={data}
                      className="p-datatable-striped"
                      onRowToggle={e => setExpandedRows(e.data)}
                      expandedRows={expandedRows}
                      rowExpansionTemplate={rowDetailForRow}
                      showGridlines
                      paginator
                      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                      totalRecords={totalRecords}
                      onPage={onPaginate}
                      first={firstItem}
                      rows={ITEMS_PER_PAGE}
                      lazy
                      loading={isFetching}
                      stateKey="techdocs-search"
                      stateStorage="session"
                      onSort={customSort}
                    >
                      <Column
                        field="name"
                        header="Name"
                        body={linkTemplateForRow}
                        style={{ width: '25%' }}
                      />
                      {columns[CatalogQueryType.TechDocs].map((col, index) => {
                        if (col?.renderer) {
                          return col.renderer(col, index);
                        }
                        return (
                          <Column
                            key={index}
                            field={col.field}
                            header={col.header}
                            style={{ width: col.width }}
                          />
                        );
                      })}
                      <Column
                        field="starred"
                        header="Starred"
                        body={starButtonTemplate}
                        style={{ width: '1%' }}
                        sortable
                      />
                    </DataTable>
                  </div>
                </div>
              </DevHubBox>
            </DevHubGrid>
          </DevHubGrid>
        </DevHubPaper>
      </Content>
    </Page>
  );
};
